import { useEffect, useState } from "react";
import type { MissionId } from "../game/dailies";
import type { DifficultyId } from "../game/difficulty";
import type { StageId } from "../game/stages";
import type { PowerId, VehicleId } from "../game/vehicles";
import { de } from "./de";
import { en } from "./en";
import { es } from "./es";
import { fr } from "./fr";
import { it } from "./it";

export type Locale = "en" | "fr" | "it" | "es" | "de";
export type MsgKey = keyof typeof en;
type Dict = { [K in MsgKey]: string };

export const LOCALE_LIST: { id: Locale; nameKey: MsgKey }[] = [
  { id: "en", nameKey: "langEn" },
  { id: "fr", nameKey: "langFr" },
  { id: "it", nameKey: "langIt" },
  { id: "es", nameKey: "langEs" },
  { id: "de", nameKey: "langDe" },
];

const TABLES: Record<Locale, Dict> = { en, fr, it, es, de };
const STORAGE = "lanedash.locale";
const listeners = new Set<() => void>();

function isLocale(v: string | null): v is Locale {
  return v === "en" || v === "fr" || v === "it" || v === "es" || v === "de";
}

function detect(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE);
    if (isLocale(saved)) return saved;
  } catch {
    /* ignore */
  }
  const nav =
    typeof navigator !== "undefined" ? navigator.language || "en" : "en";
  const code = nav.slice(0, 2).toLowerCase();
  if (isLocale(code)) return code;
  return "en";
}

let locale: Locale = detect();

function applyDocLang() {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}
applyDocLang();

export function getLocale(): Locale {
  return locale;
}

export function setLocale(next: Locale) {
  if (next === locale) return;
  locale = next;
  try {
    localStorage.setItem(STORAGE, next);
  } catch {
    /* ignore */
  }
  applyDocLang();
  for (const fn of listeners) fn();
}

export function cycleLocale() {
  const i = LOCALE_LIST.findIndex((l) => l.id === locale);
  setLocale(LOCALE_LIST[(i + 1) % LOCALE_LIST.length]!.id);
}

export function t(key: MsgKey, vars?: Record<string, string | number>): string {
  let s = TABLES[locale][key] ?? en[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}

export function subscribeLocale(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Subscribe in a root component so the tree re-renders on language change. */
export function useLocale(): Locale {
  const [loc, setLoc] = useState(locale);
  useEffect(() => subscribeLocale(() => setLoc(getLocale())), []);
  return loc;
}

export function tDiff(id: DifficultyId) {
  if (id === "easy") return t("diffEasy");
  if (id === "hard") return t("diffHard");
  return t("diffMedium");
}

export function tDiffHint(id: DifficultyId) {
  if (id === "easy") return t("hintEasy");
  if (id === "hard") return t("hintHard");
  return t("hintMedium");
}

export function tMission(id: MissionId) {
  const key = `mission_${id}` as MsgKey;
  return t(key);
}

const VEH_BLURB: Record<VehicleId, MsgKey> = {
  dash: "veh_dash_blurb",
  bulldog: "veh_bulldog_blurb",
  falcon: "veh_falcon_blurb",
  magneto: "veh_magneto_blurb",
  wasp: "veh_wasp_blurb",
  phantom: "veh_phantom_blurb",
};

export function tVehicleBlurb(id: VehicleId) {
  return t(VEH_BLURB[id]);
}

export function tPowerBlurb(id: VehicleId, power: PowerId) {
  if (id === "wasp") return t("pow_none_wasp");
  if (power === "crush_crates") return t("pow_crush");
  if (power === "glider") return t("pow_glider");
  if (power === "magnet") return t("pow_magnet");
  if (power === "phase") return t("pow_phase");
  return t("pow_none");
}

const STAGE_NAME: Record<StageId, MsgKey> = {
  neon: "stage_neon",
  lunar: "stage_lunar",
  volcano: "stage_volcano",
  ice: "stage_ice",
};
const STAGE_BLURB: Record<StageId, MsgKey> = {
  neon: "stage_neon_blurb",
  lunar: "stage_lunar_blurb",
  volcano: "stage_volcano_blurb",
  ice: "stage_ice_blurb",
};

export function tStageName(id: StageId) {
  return t(STAGE_NAME[id]);
}

export function tStageBlurb(id: StageId) {
  return t(STAGE_BLURB[id]);
}

export function tCurrentLocaleName() {
  const row = LOCALE_LIST.find((l) => l.id === locale)!;
  return t(row.nameKey);
}

export function tPowerName(id: PowerId) {
  if (id === "magnet") return t("powerMagnet");
  if (id === "phase") return t("powerPhase");
  return t("powerDefault");
}
